interface Props {
  /** Chuỗi số theo thứ tự thời gian. Rỗng hoặc toàn số 0 thì vẽ một đường phẳng. */
  points: number[]
  /** Tên token màu, không phải mã hex — để bộ chủ đề tối đổi theo. */
  tone: 'brand' | 'clay' | 'violet'
}

const W = 120
const H = 44

/**
 * Đường nhỏ nằm sau con số ở thẻ thống kê.
 *
 * Cố ý không có trục, không có nhãn, không có chấm dữ liệu: nó chỉ trả lời một câu
 * "hôm nay đang đi lên hay đứng yên". Ai cần con số chính xác thì đọc ngay con số to
 * bên cạnh, hoặc mở báo cáo. Thêm trục vào đây là biến một nét trang trí thành một
 * bảng số đọc dở.
 *
 * Vẽ thẳng bằng SVG, không kéo thư viện đồ thị: máy quầy chạy trên điện thoại Android rẻ
 * và app phải mở được lúc mất mạng, nên mỗi ki-lô-bai tải về đều phải đáng.
 */
export function Sparkline({ points, tone }: Props) {
  const id = `spark-${tone}`
  const max = Math.max(...points, 0)

  // Chưa bán gì thì không vẽ gì. Một đường phẳng nằm ngang giữa thẻ trông y hệt một
  // cái gạch chân lạc chỗ, mà nó lại còn nói dối: nhìn như có số liệu và số liệu bằng 0
  // suốt cả ngày, trong khi thật ra là chưa có đơn nào.
  if (points.length < 2 || max <= 0) return null

  const series = points
  const step = W / (series.length - 1)

  const xy = series.map((v, i) => {
    const x = i * step
    // Chừa 3px trên và dưới: đường chạm mép khung trông như bị cắt cụt.
    const y = H - 3 - (v / max) * (H - 6)
    return `${x.toFixed(1)},${y.toFixed(1)}`
  })

  const line = `M${xy.join(' L')}`
  const area = `${line} L${W},${H} L0,${H} Z`

  return (
    <svg
      className="spark"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={`var(--${tone})`} stopOpacity="0.28" />
          <stop offset="100%" stopColor={`var(--${tone})`} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${id})`} />
      <path
        d={line}
        fill="none"
        stroke={`var(--${tone})`}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.55"
      />
    </svg>
  )
}
